import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatDistanceToNow, format, parseISO } from "date-fns";
import { ShieldAlert, RefreshCcw, Trash2, EyeOff, Undo2, Sparkles, ShieldCheck, TrendingUp, MessageSquareWarning, Users2, MapPin } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const FILTERS = [
  { id: "hidden", label: "Hidden", desc: "Auto-hidden by moderation" },
  { id: "caution", label: "Caution", desc: "Flagged but visible" },
  { id: "flagged", label: "All flagged", desc: "Hidden + Caution" },
  { id: "all", label: "All posts" },
];

function relTime(iso) {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ""; }
}

export default function AdminModeration() {
  const { user, loading } = useAuth();
  const [status, setStatus] = useState("hidden");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [action, setAction] = useState({});
  const [insights, setInsights] = useState(null);
  const [insightsError, setInsightsError] = useState(false);

  const load = async (s = status) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/moderation?status=${s}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const loadInsights = async () => {
    setInsightsError(false);
    try {
      const { data } = await api.get("/admin/moderation/insights");
      setInsights(data);
    } catch (_e) {
      setInsightsError(true);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") { load(status); loadInsights(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, user]);

  if (loading) return <div className="grid min-h-[60vh] place-items-center">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const doAction = async (post, kind) => {
    setAction((a) => ({ ...a, [post.id]: kind }));
    try {
      if (kind === "restore") {
        await api.post(`/admin/posts/${post.id}/restore`);
        toast.success("Restored to feed");
      } else if (kind === "hide") {
        await api.post(`/admin/posts/${post.id}/hide`);
        toast.success("Hidden from feed");
      } else if (kind === "rerun") {
        await api.post(`/feed/${post.id}/moderate`);
        toast.success("Re-scanned by Claude");
      } else if (kind === "delete") {
        if (!window.confirm("Permanently delete this post and its comments?")) {
          setAction((a) => ({ ...a, [post.id]: null }));
          return;
        }
        await api.delete(`/admin/posts/${post.id}`);
        toast.success("Post deleted");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAction((a) => ({ ...a, [post.id]: null }));
    }
  };

  return (
    <div data-testid="admin-moderation-page" className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
      <header className="mb-6">
        <div className="jirani-eyebrow">Admin</div>
        <h1
          className="mt-2 flex items-center gap-3 text-4xl tracking-tight text-[#1A1A1A]"
          style={{ fontFamily: "Outfit", fontWeight: 800 }}
        >
          <ShieldAlert className="h-8 w-8 text-[#D95D39]" /> Moderation Queue
        </h1>
        <p className="mt-2 max-w-xl text-[#4A4A4A]">
          Review posts that Claude has flagged as risky or borderline. Restore the ones that are fine,
          delete anything harmful.
        </p>
      </header>

      <div className="mb-6 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            data-testid={`admin-filter-${f.id}`}
            onClick={() => setStatus(f.id)}
            className={`jirani-pill rounded-full px-4 py-1.5 text-xs font-medium ${
              status === f.id
                ? "bg-[#2E473B] text-white"
                : "border border-black/10 bg-white text-[#2E473B] hover:bg-[#F9F7F4]"
            }`}
            title={f.desc || ""}
          >
            {f.label}
          </button>
        ))}
      </div>

      {insightsError && (
        <div
          data-testid="admin-insights-error"
          className="mb-6 flex items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          <span>Couldn't load community analytics. Please try again.</span>
          <Button
            data-testid="admin-insights-retry"
            onClick={loadInsights}
            variant="outline"
            className="jirani-pill rounded-full border-red-200 bg-white px-4 text-red-700 hover:bg-red-100"
          >
            <RefreshCcw className="mr-1.5 h-4 w-4" /> Retry
          </Button>
        </div>
      )}

      {insights && (
        <section data-testid="admin-insights" className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Chart card */}
          <div className="jirani-card lg:col-span-2 rounded-2xl border border-black/5 bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="jirani-eyebrow">Last 14 days</div>
                <div className="mt-1 flex items-center gap-2 text-sm font-semibold text-[#1A1A1A]" style={{ fontFamily: "Outfit" }}>
                  <TrendingUp className="h-4 w-4 text-[#D95D39]" /> Flagged posts per day
                </div>
              </div>
              <div className="flex gap-4 text-xs text-[#2E473B]/80">
                <div className="flex items-center gap-1">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#F4A261]" /> caution
                </div>
                <div className="flex items-center gap-1">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#C1292E]" /> risky
                </div>
              </div>
            </div>
            <div className="h-52" data-testid="admin-insights-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={insights.daily || []} barCategoryGap={4}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#00000010" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) => { try { return format(parseISO(d), "d MMM"); } catch { return d; } }}
                    tick={{ fill: "#4A4A4A", fontSize: 11 }}
                    stroke="#00000020"
                  />
                  <YAxis allowDecimals={false} tick={{ fill: "#4A4A4A", fontSize: 11 }} stroke="#00000020" />
                  <Tooltip
                    contentStyle={{ border: "1px solid #0000000d", borderRadius: 12, background: "#fff" }}
                    labelFormatter={(d) => { try { return format(parseISO(d), "EEE, d MMM"); } catch { return d; } }}
                  />
                  <Bar dataKey="caution" stackId="a" fill="#F4A261" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="risky"   stackId="a" fill="#C1292E" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3 border-t border-black/5 pt-3 text-center">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#2E473B]/70">Posts</div>
                <div className="text-lg font-semibold text-[#1A1A1A]" style={{ fontFamily: "Outfit" }}>
                  {insights.totals?.posts || 0}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#2E473B]/70">Flagged</div>
                <div className="text-lg font-semibold text-[#F4A261]" style={{ fontFamily: "Outfit" }}>
                  {insights.totals?.flagged || 0}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#2E473B]/70">Hidden</div>
                <div className="text-lg font-semibold text-[#C1292E]" style={{ fontFamily: "Outfit" }}>
                  {insights.totals?.hidden || 0}
                </div>
              </div>
            </div>
          </div>

          {/* Reasons + Offenders */}
          <div className="space-y-4">
            <div className="jirani-card rounded-2xl border border-black/5 bg-white p-5">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#1A1A1A]" style={{ fontFamily: "Outfit" }}>
                <MessageSquareWarning className="h-4 w-4 text-[#D95D39]" /> Top reasons
              </div>
              {(insights.top_reasons || []).length === 0 ? (
                <div className="text-xs text-[#4A4A4A]">Nothing yet — clean feed!</div>
              ) : (
                <ul data-testid="admin-top-reasons" className="space-y-2">
                  {insights.top_reasons.map((r, i) => (
                    <li key={i} className="rounded-xl bg-[#F9F7F4] p-2.5 text-xs">
                      <div className="flex items-start justify-between gap-2">
                        <div className="line-clamp-2 text-[#1A1A1A]">{r.reason}</div>
                        <div className="shrink-0 rounded-full bg-white px-1.5 py-0.5 text-[10px] font-semibold text-[#2E473B]">
                          ×{r.count}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="jirani-card rounded-2xl border border-black/5 bg-white p-5">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#1A1A1A]" style={{ fontFamily: "Outfit" }}>
                <Users2 className="h-4 w-4 text-[#D95D39]" /> Repeat offenders
              </div>
              {(insights.repeat_offenders || []).length === 0 ? (
                <div className="text-xs text-[#4A4A4A]">No repeats — everyone's behaving.</div>
              ) : (
                <ul data-testid="admin-repeat-offenders" className="space-y-2">
                  {insights.repeat_offenders.map((o) => (
                    <li key={o.user_id} className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="grid h-8 w-8 place-items-center rounded-full bg-[#2E473B] text-xs font-semibold text-white">
                          {(o.name || "?").charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-[#1A1A1A]">{o.name}</div>
                          {o.neighborhood && (
                            <div className="text-[11px] text-[#2E473B]/70">{o.neighborhood}</div>
                          )}
                        </div>
                      </div>
                      <div className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800">
                        {o.count} flagged
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}

      {insights && insights.by_community && insights.by_community.length > 0 && (
        <section data-testid="admin-community-breakdown" className="mb-8">
          <div className="jirani-card rounded-2xl border border-black/5 bg-white p-5">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="jirani-eyebrow">By community</div>
                <div className="mt-1 flex items-center gap-2 text-sm font-semibold text-[#1A1A1A]" style={{ fontFamily: "Outfit" }}>
                  <MapPin className="h-4 w-4 text-[#D95D39]" /> Where content needs attention
                </div>
              </div>
              <div className="flex gap-4 text-xs text-[#2E473B]/80">
                <div className="flex items-center gap-1">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#F4A261]" /> caution
                </div>
                <div className="flex items-center gap-1">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#C1292E]" /> risky
                </div>
              </div>
            </div>

            <div className="h-64" data-testid="admin-community-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={insights.by_community} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#00000010" />
                  <XAxis type="number" allowDecimals={false} tick={{ fill: "#4A4A4A", fontSize: 11 }} stroke="#00000020" />
                  <YAxis type="category" dataKey="community" width={120} tick={{ fill: "#1A1A1A", fontSize: 11 }} stroke="#00000020" />
                  <Tooltip
                    contentStyle={{ border: "1px solid #0000000d", borderRadius: 12, background: "#fff" }}
                    formatter={(v, n) => [v, n]}
                  />
                  <Bar dataKey="caution" stackId="c" fill="#F4A261" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="risky"   stackId="c" fill="#C1292E" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-left text-xs" data-testid="admin-community-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-[#2E473B]/70">
                    <th className="px-3 py-2">Community</th>
                    <th className="px-3 py-2 text-right">Posts</th>
                    <th className="px-3 py-2 text-right">Flagged</th>
                    <th className="px-3 py-2 text-right">Hidden</th>
                    <th className="px-3 py-2 text-right">Flag rate</th>
                  </tr>
                </thead>
                <tbody>
                  {insights.by_community.map((c) => {
                    const rate = c.posts ? Math.round((c.flagged / c.posts) * 100) : 0;
                    return (
                      <tr
                        key={c.community}
                        data-testid={`admin-community-row-${c.community}`}
                        className="border-t border-black/5 text-[#1A1A1A]"
                      >
                        <td className="px-3 py-2 font-medium">{c.community}</td>
                        <td className="px-3 py-2 text-right">{c.posts}</td>
                        <td className="px-3 py-2 text-right text-[#F4A261]">{c.flagged}</td>
                        <td className="px-3 py-2 text-right text-[#C1292E]">{c.hidden}</td>
                        <td className="px-3 py-2 text-right">
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            rate >= 20 ? "bg-red-100 text-red-800"
                            : rate >= 10 ? "bg-amber-100 text-amber-800"
                            : "bg-emerald-100 text-emerald-800"
                          }`}>{rate}%</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {busy ? (
        <div className="grid gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-40 animate-pulse rounded-2xl border border-black/5 bg-white/60" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="admin-empty"
          className="rounded-2xl border border-dashed border-black/10 bg-white p-10 text-center text-[#4A4A4A]"
        >
          Nothing to review here — the community is looking tidy!
        </div>
      ) : (
        <ul data-testid="admin-list" className="space-y-4">
          {items.map((p) => {
            const mod = p.moderation || {};
            const busyKind = action[p.id];
            return (
              <li
                key={p.id}
                data-testid={`admin-post-${p.id}`}
                className="jirani-card rounded-2xl border border-black/5 bg-white p-5"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-[#1A1A1A]">
                      {p.author?.name || "A neighbor"}
                      {p.author?.neighborhood && (
                        <span className="text-xs text-[#2E473B]/70"> · {p.author.neighborhood}</span>
                      )}
                      <span className="text-xs text-[#4A4A4A]"> · {relTime(p.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {mod.risk_level === "risky" && (
                      <Badge className="rounded-full bg-red-100 text-red-800 hover:bg-red-100">
                        <ShieldAlert className="mr-1 h-3 w-3" /> risky
                      </Badge>
                    )}
                    {mod.risk_level === "caution" && (
                      <Badge className="rounded-full bg-amber-100 text-amber-800 hover:bg-amber-100">
                        <ShieldAlert className="mr-1 h-3 w-3" /> caution
                      </Badge>
                    )}
                    {mod.risk_level === "safe" && (
                      <Badge className="rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                        <ShieldCheck className="mr-1 h-3 w-3" /> safe
                      </Badge>
                    )}
                    <Badge className="rounded-full bg-[#F9F7F4] text-[#2E473B] hover:bg-[#F9F7F4]">
                      {p.hidden ? "hidden" : "visible"}
                    </Badge>
                  </div>
                </div>

                <p className="mt-3 whitespace-pre-wrap text-[#1A1A1A]">{p.content}</p>

                {mod.reason && (
                  <div className="mt-3 rounded-xl bg-[#F9F7F4] p-3 text-xs text-[#2E473B]">
                    <div className="mb-1 inline-flex items-center gap-1 font-semibold uppercase tracking-wider">
                      <Sparkles className="h-3 w-3 text-[#D95D39]" /> Claude noted
                    </div>
                    <div>{mod.reason}</div>
                  </div>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {p.hidden ? (
                    <Button
                      data-testid={`admin-restore-${p.id}`}
                      onClick={() => doAction(p, "restore")}
                      disabled={!!busyKind}
                      className="jirani-pill rounded-full bg-[#2E473B] px-4 text-white hover:bg-[#243830]"
                    >
                      <Undo2 className="mr-1.5 h-4 w-4" /> Restore
                    </Button>
                  ) : (
                    <Button
                      data-testid={`admin-hide-${p.id}`}
                      onClick={() => doAction(p, "hide")}
                      disabled={!!busyKind}
                      variant="outline"
                      className="jirani-pill rounded-full border-black/10 bg-white px-4 text-[#2E473B]"
                    >
                      <EyeOff className="mr-1.5 h-4 w-4" /> Hide
                    </Button>
                  )}
                  <Button
                    data-testid={`admin-rerun-${p.id}`}
                    onClick={() => doAction(p, "rerun")}
                    disabled={!!busyKind}
                    variant="outline"
                    className="jirani-pill rounded-full border-black/10 bg-white px-4 text-[#2E473B]"
                  >
                    <RefreshCcw className={`mr-1.5 h-4 w-4 ${busyKind === "rerun" ? "animate-spin" : ""}`} /> Re-scan
                  </Button>
                  <Button
                    data-testid={`admin-delete-${p.id}`}
                    onClick={() => doAction(p, "delete")}
                    disabled={!!busyKind}
                    variant="outline"
                    className="jirani-pill ml-auto rounded-full border-red-200 bg-white px-4 text-[#C1292E] hover:bg-red-50"
                  >
                    <Trash2 className="mr-1.5 h-4 w-4" /> Delete
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
